/* eslint-disable react/no-danger */
import * as React from 'react';
import moment from 'moment-timezone';
import { Lieferantenrechnung, useLieferantenrechnungList } from 'api/lieferantenrechnung';
import {
    LieferantenrechnungAraPositionFilter,
    useLieferantenrechnungAraPositionList,
} from 'api/lieferantenrechnungAraPosition';
import classnames from 'classnames';
import { formStyles } from 'styles/formStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { createRecordUrl, formatDecimal, formatMoney } from 'utils/formatter';
import { einheitenByCodeSelector, lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        borderBottom: '0.5px solid #CCC',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    borderTop: {
        borderTop: '1px solid',
    },
    borderBottom: {
        borderBottom: '1px solid',
    },
    noPadding: {
        paddingTop: 0,
        paddingBottom: 0,
    },
});

interface ReclayZahlungProps {
    abrJahr: number;
    abrMo: number;
    showDetails: boolean;
    printView?: boolean;
}

export const ReclayZahlung: React.FC<ReclayZahlungProps> = ({ abrJahr, abrMo, showDetails, printView = false }) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    const monthStart = moment(`${abrJahr.toString()}-${abrMo.toString()}-01`);
    const monthEnd = monthStart.clone().endOf('month');

    const lieferantenrechnungList = useLieferantenrechnungList({
        datumVon: monthStart.format('YYYY-MM-DD'),
        datumBis: monthEnd.format('YYYY-MM-DD'),
        withLieferant: true,
    });

    const lieferantenrechnungAraPositionFilter = React.useMemo<LieferantenrechnungAraPositionFilter>(
        () => ({
            rNrList: (lieferantenrechnungList.data || []).map((r) => r.nr),
        }),
        [lieferantenrechnungList],
    );

    const lieferantenrechnungAraPositionList = useLieferantenrechnungAraPositionList(
        lieferantenrechnungAraPositionFilter,
        {
            enabled: lieferantenrechnungList.isSuccess,
        },
    );

    const moneyClassNameHead = classnames(
        styles.tdHead,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );

    const moneyClassNameBody = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );

    const renderHeaderRow = () => {
        return (
            <tr>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportRNrPrintCell : formStyles.reportRNrCell,
                    )}
                >
                    ER-Nr.
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                >
                    Lieferant
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportPosPrintCell : formStyles.reportPosCell,
                    )}
                >
                    ER-Pos
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView
                            ? formStyles.reportReclayArtikelTextPrintCell
                            : formStyles.reportReclayArtikelTextCell,
                    )}
                >
                    Reclay Artikel
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportEinheitCell : formStyles.reportEinheitCell,
                    )}
                >
                    Einheit
                </td>
                <td className={classnames(moneyClassNameHead)}>Menge</td>
                <td className={classnames(moneyClassNameHead)}>PR./Einh.</td>
                <td className={classnames(moneyClassNameHead)}>Preis</td>
            </tr>
        );
    };

    const renderLieferantenrechnungLines = (lieferantenrechnung: Lieferantenrechnung) => {
        const araPositionen = (lieferantenrechnungAraPositionList.data || [])
            .filter((p) => p.rNr === lieferantenrechnung.nr && p.posMenge !== 0)
            .sort((a, b) =>
                (a.rPos.toString().padStart(4, '0') + a.posText).localeCompare(
                    b.rPos.toString().padStart(4, '0') + b.posText,
                ),
            );

        if (araPositionen.length === 0) {
            return null;
        }

        return (
            <>
                {araPositionen.map((araPosition) => (
                    <tr>
                        <td
                            className={classnames(
                                styles.tdBody,
                                printView ? formStyles.reportRNrPrintCell : formStyles.reportRNrCell,
                            )}
                        >
                            <OpenLink
                                link={createRecordUrl('lieferantenrechnungen', undefined, araPosition.rNr)}
                                label={araPosition.rNr}
                            />
                        </td>
                        <td
                            className={classnames(
                                styles.tdBody,
                                printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                            )}
                        >
                            {lieferantenrechnung.lieferant?.name1}
                        </td>
                        <td
                            className={classnames(
                                styles.tdBody,
                                printView ? formStyles.reportPosPrintCell : formStyles.reportPosCell,
                            )}
                        >
                            {araPosition.rPos}
                        </td>
                        <td
                            className={classnames(
                                styles.tdBody,
                                printView
                                    ? formStyles.reportReclayArtikelTextPrintCell
                                    : formStyles.reportReclayArtikelTextCell,
                            )}
                        >
                            {araPosition.posText}
                        </td>
                        <td
                            className={classnames(
                                styles.tdBody,
                                printView ? formStyles.reportEinheitCell : formStyles.reportEinheitCell,
                            )}
                        >
                            {araPosition.posEinh
                                ? einheitenByCode[araPosition.posEinh].text || araPosition.posEinh
                                : '?'}
                        </td>
                        <td className={classnames(moneyClassNameBody)}>{formatDecimal(araPosition.posMenge, 3)}</td>
                        <td className={classnames(moneyClassNameBody)}>{formatMoney(araPosition.posEPreis, 3)}</td>
                        <td className={classnames(moneyClassNameBody)}>
                            {formatMoney(araPosition.posMenge * araPosition.posEPreis, 2)}
                        </td>
                    </tr>
                ))}
                <tr>
                    <td colSpan={16} className={classnames(styles.borderBottom, styles.noPadding)} />
                </tr>
            </>
        );
    };

    const renderSummary = () => {
        const summaryLines: {
            [key: string]: { posText: string; posEinh: string; posEPreis: number; posMenge: number };
        } = {};

        (lieferantenrechnungAraPositionList.data || [])
            .filter((p) => p.posMenge !== 0)
            .sort((a, b) => a.posText.localeCompare(b.posText))
            .forEach((araPosition) => {
                if (!summaryLines[araPosition.artikelNr]) {
                    summaryLines[araPosition.artikelNr] = {
                        posText: araPosition.posText,
                        posEinh: araPosition.posEinh,
                        posEPreis: araPosition.posEPreis,
                        posMenge: 0,
                    };
                }

                summaryLines[araPosition.artikelNr].posMenge += araPosition.posMenge;
            });

        return Object.values(summaryLines).map((summaryLine) => (
            <tr>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView
                            ? formStyles.reportReclayArtikelTextPrintCell
                            : formStyles.reportReclayArtikelTextCell,
                    )}
                >
                    {summaryLine.posText}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportEinheitCell : formStyles.reportEinheitCell,
                    )}
                >
                    {summaryLine.posEinh ? einheitenByCode[summaryLine.posEinh].text || summaryLine.posEinh : '?'}
                </td>
                <td className={classnames(moneyClassNameBody)}>{formatDecimal(summaryLine.posMenge, 3)}</td>
                <td className={classnames(moneyClassNameBody)}>{formatMoney(summaryLine.posEPreis, 3)}</td>
                <td className={classnames(moneyClassNameBody)}>
                    {formatMoney(summaryLine.posMenge * summaryLine.posEPreis, 2)}
                </td>
            </tr>
        ));
    };

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            {!printView && showDetails && (
                <table className={formStyles.reportFixedHeaderNoBgColor}>{renderHeaderRow()}</table>
            )}
            <h1>
                Reclay-Zahlungen {abrJahr}-{abrMo.toString().padStart(2, '0')}
            </h1>
            {printView && (
                <table className={formStyles.reportFilterInfo}>
                    <tr>
                        <td>Druckdatum: </td>
                        <td>{moment().format('DD.MM.YYYY HH:mm:ss')}</td>
                    </tr>
                </table>
            )}
            <table className={formStyles.reportTable}>
                <thead>{renderHeaderRow()}</thead>
                <tbody>
                    <tr>
                        <td colSpan={16} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    {showDetails &&
                        (lieferantenrechnungList.data || [])
                            .sort((a, b) => a.nr.localeCompare(b.nr))
                            .map((lieferantenrechnung) => renderLieferantenrechnungLines(lieferantenrechnung))}

                    <tr>
                        <td colSpan={16} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>

                    {renderSummary()}
                </tbody>
            </table>
        </div>
    );
};
